import type { Ref } from "react";

import { useTranslation } from "react-i18next";

import type { Session } from "@/api/bale.zod";

import { useDeleteSession, useListSessionsSuspense } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Badge } from "@/components/Badge";
import { CardSubtitleHeader } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { IconButton } from "@/components/IconButton";

export function ManageSessionsModal({ ref, onClose }: { ref: Ref<HTMLDialogElement>; onClose?: () => void }) {
  const { t } = useTranslation();
  const sessions = useListSessionsSuspense({ query: { select: (data) => data.data } });

  return (
    <Dialog ref={ref} onClose={onClose} closeOnOutside className="w-2xl">
      <CardSubtitleHeader>{t("manage-sessions")}</CardSubtitleHeader>
      <div className="flex flex-col">
        <p className="p-4 pt-0 font-disinterest">{t("manage-sessions-description")}</p>
        <div className="flex flex-col border-t border-divider empty:hidden">
          {sessions.data.sessions.map((session) => (
            <SessionItem
              key={session.id}
              session={session}
              isCurrentSession={session.id === sessions.data.current_session_id}
            />
          ))}
        </div>
      </div>
    </Dialog>
  );
}

function SessionItem({ session, isCurrentSession }: { session: Session; isCurrentSession: boolean }) {
  const { t } = useTranslation();
  const deleteSession = useDeleteSession();

  return (
    <div className="flex flex-row items-center justify-between border-b border-divider px-4 py-2 text-start">
      <div className="flex flex-col gap-1">
        <div className="flex flex-row gap-4">
          <p className="font-interest">
            {session.id.slice(0, 4)}...{session.id.slice(-4)}
          </p>
          {isCurrentSession && <Badge>{t("current-session")}</Badge>}
        </div>
        <div className="flex flex-row gap-4">
          <p className="font-hint text-placeholder">{t("created-datetime", { date: session.created_at })}</p>
          <p className="font-hint text-placeholder">{t("expires-datetime", { date: session.expires_at })}</p>
        </div>
      </div>
      <ActionBox>
        <IconButton
          type="button"
          className="text-danger"
          name="trash"
          disabled={isCurrentSession}
          onClick={() => deleteSession.mutate({ sessionId: session.id })}
        />
      </ActionBox>
    </div>
  );
}
